/**
 * File: clientQuotesService.ts
 * Path: apps/admin/src/shared/services/clientQuotesService.ts
 * Description: Reads a client's saved quotes. Read-only: the quotes feature
 * owns the `quotes` collection, its writes and its rules.
 */

import { FirestoreService, limit, where } from "@idk/config";
import {
  mergeQuotes,
  toClientQuote,
  type ClientQuote,
} from "../utils/clientQuoteUtils";

/** Named by the quotes feature; kept local so this page never writes to it. */
const QUOTES = "quotes";

/** Plenty for one client; equality only, so no composite index is needed. */
const PER_CLIENT_LIMIT = 50;

type Stored = Record<string, unknown> & { id: string };

const quotesWhere = async (
  field: "clientId" | "leadId",
  id: string,
): Promise<ClientQuote[]> => {
  const rows = await FirestoreService.query<Stored>(
    QUOTES,
    where(field, "==", id),
    limit(PER_CLIENT_LIMIT),
  );
  return rows.map((row) => toClientQuote(row.id, row));
};

export const clientQuotesRepo = {
  /** Quotes for the client, and for the lead they were won from (if any). */
  forClient: async (
    clientId: string,
    leadId: string | null,
  ): Promise<ClientQuote[]> => {
    const [own, fromLead] = await Promise.all([
      quotesWhere("clientId", clientId),
      leadId ? quotesWhere("leadId", leadId) : Promise.resolve([]),
    ]);
    return mergeQuotes(own, fromLead);
  },
};
